// src/pages/FundPlanner/lib/solverTrim.js
import dayjs from "lib/dayjs";
import {
  SLGS_DENOM_UNIT,
  SLGS_ROUND_MODE,
  roundToDenomination,
  dlog,
  dgroupCollapsed,
  dend,
} from "./solverMain";

/* ─────────────────────────────────────────────
   Public API
───────────────────────────────────────────── */

export function buildInitialPrincipalsFromLP({
  candidates,
  lpVars,
  denomination = SLGS_DENOM_UNIT,
  roundMode = SLGS_ROUND_MODE || "ceil",
}) {
  if (!Array.isArray(candidates) || !lpVars) return [];

  const principals = candidates.map((cand) => {
    const x = Number(lpVars[cand.varName] ?? 0);
    if (!isFinite(x) || x <= 0) return 0;
    const P = roundToDenomination(x, denomination, roundMode);
    return P > 0 ? P : 0;
  });

  return principals;
}

/**
 * Revised Net Funding Metrics
 * ---------------------------------------------
 * Key change in this version:
 *  • Remove dayjs() from inner loops by using ISO date string compares.
 */
export function computeNetFundingMetrics({
  candidates,
  principals,
  periods,
  settleDate,
}) {
  if (
    !Array.isArray(candidates) ||
    !Array.isArray(principals) ||
    !Array.isArray(periods) ||
    candidates.length === 0 ||
    periods.length === 0
  ) {
    return {
      deposit: 0,
      cost: 0,
      minBalance: 0,
      finalBalance: 0,
      balances: [],
      totalPrincipal: 0,
      feasible: true,
    };
  }

  const P = principals.map((p) => Number(p) || 0);
  const totalPrincipal = P.reduce((s, p) => s + p, 0);

  // Use ISO date strings for comparisons (fast)
  let prevStr = dayjs(settleDate).startOf("day").format("YYYY-MM-DD");
  let balance = 0;
  const balances = [];

  for (let i = 0; i < periods.length; i++) {
    const { T_i, F_i } = periods[i];

    // periods[i].T_i is dayjs already in solverNet, but make robust:
    const tiStr = dayjs(T_i).startOf("day").format("YYYY-MM-DD");
    const drawAmt = Number(F_i) || 0;

    let inflowThisPeriod = 0;

    for (let k = 0; k < candidates.length; k++) {
      const principal = P[k];
      if (!principal || principal <= 0) continue;

      const flows = candidates[k]?.inflowsPerDollar;
      if (!Array.isArray(flows) || flows.length === 0) continue;

      for (let f = 0; f < flows.length; f++) {
        const cf = flows[f];
        const dStr = cf?.date;
        if (!dStr) continue;

        // ISO lexicographic compare is valid for YYYY-MM-DD
        if (dStr > prevStr && dStr <= tiStr) {
          const cfAmtPerDollar = Number(cf.amount) || 0;
          inflowThisPeriod += roundCents(principal * cfAmtPerDollar);
        }
      }
    }

    balance += inflowThisPeriod - drawAmt;
    balances.push(balance);

    prevStr = tiStr;
  }

  if (!balances.length) {
    return {
      deposit: 0,
      cost: totalPrincipal,
      minBalance: 0,
      finalBalance: 0,
      balances: [],
      totalPrincipal,
      feasible: true,
    };
  }

  const finalBalanceRaw = balances[balances.length - 1];
  let minBalanceRaw = Infinity;
  for (let i = 0; i < balances.length; i++) {
    const b = balances[i];
    if (b < minBalanceRaw) minBalanceRaw = b;
  }

  const depositRawNeeded = Math.max(-finalBalanceRaw, -minBalanceRaw, 0);
  const depositRounded = roundCents(depositRawNeeded);

  const finalBalance = roundCents(finalBalanceRaw + depositRounded);
  const minBalance = roundCents(minBalanceRaw + depositRounded);

  const feasible = minBalance >= 0;

  const cost = feasible
    ? roundCents(totalPrincipal + depositRounded)
    : Number.POSITIVE_INFINITY;

  return {
    deposit: depositRounded,
    cost,
    minBalance,
    finalBalance,
    balances,
    totalPrincipal,
    feasible,
  };
}

export function trimNetFundingSolution({
  candidates,
  periods,
  settleDate,
  initialPrincipals,
  denomination = SLGS_DENOM_UNIT,
  maxIterations = 1000,
  log = false,
}) {
  const n = Array.isArray(candidates) ? candidates.length : 0;
  if (!n || !Array.isArray(initialPrincipals) || !Array.isArray(periods)) {
    return {
      principals: initialPrincipals || [],
      deposit: 0,
      cost: 0,
      minBalance: 0,
      finalBalance: 0,
      balances: [],
      iterations: 0,
      __perf: {
        totalMs: 0,
        computeMetricsMs: 0,
        computeMetricsCalls: 0,
        iters: 0,
        movesTested: 0,
        singleMovesTested: 0,
        pairedMovesTested: 0,
      },
    };
  }

  const t0All = performance.now();
  const perf = {
    totalMs: 0,
    computeMetricsMs: 0,
    computeMetricsCalls: 0,
    iters: 0,
    movesTested: 0,
    singleMovesTested: 0,
    pairedMovesTested: 0,
    meta: {
      n,
      periods: periods.length,
      maxIterations,
      denomination,
    },
  };

  const timedMetrics = (args) => {
    const t = performance.now();
    const out = computeNetFundingMetrics(args);
    perf.computeMetricsMs += performance.now() - t;
    perf.computeMetricsCalls += 1;
    return out;
  };

  const P0 = new Array(n)
    .fill(0)
    .map((_, i) => Number(initialPrincipals[i]) || 0);

  let current = {
    principals: P0,
    ...timedMetrics({
      candidates,
      principals: P0,
      periods,
      settleDate,
    }),
  };

  if (log) {
    dgroupCollapsed("[TRIM] Initial discrete solution");
    dlog({
      totalPrincipal: current.totalPrincipal,
      deposit: current.deposit,
      cost: current.cost,
      minBalance: current.minBalance,
      finalBalance: current.finalBalance,
    });
    dend();
  }

  let iterations = 0;

  while (iterations < maxIterations) {
    iterations++;
    perf.iters = iterations;

    let best = current;
    let bestMove = null;

    for (let k = 0; k < n; k++) {
      const basePk = current.principals[k];

      {
        const Pplus = current.principals.slice();
        Pplus[k] = basePk + denomination;

        const metrics = timedMetrics({
          candidates,
          principals: Pplus,
          periods,
          settleDate,
        });

        perf.movesTested += 1;
        perf.singleMovesTested += 1;

        if (isBetter(metrics, best)) {
          best = { principals: Pplus, ...metrics };
          bestMove = { type: "single+", k };
        }
      }

      if (basePk >= denomination) {
        const Pminus = current.principals.slice();
        Pminus[k] = basePk - denomination;

        const metrics = timedMetrics({
          candidates,
          principals: Pminus,
          periods,
          settleDate,
        });

        perf.movesTested += 1;
        perf.singleMovesTested += 1;

        if (isBetter(metrics, best)) {
          best = { principals: Pminus, ...metrics };
          bestMove = { type: "single-", k };
        }
      }
    }

    for (let k = 0; k < n; k++) {
      for (let j = 0; j < n; j++) {
        if (k === j) continue;
        if (current.principals[j] < denomination) continue;

        const Ppair = current.principals.slice();
        Ppair[k] += denomination;
        Ppair[j] -= denomination;

        const metrics = timedMetrics({
          candidates,
          principals: Ppair,
          periods,
          settleDate,
        });

        perf.movesTested += 1;
        perf.pairedMovesTested += 1;

        if (isBetter(metrics, best)) {
          best = { principals: Ppair, ...metrics };
          bestMove = { type: "pair", k, j };
        }
      }
    }

    if (!bestMove || approxEqual(best.cost, current.cost)) break;

    current = best;
  }

  perf.totalMs = performance.now() - t0All;

  if (log) {
    dgroupCollapsed("[TRIM] Final discrete solution");
    dlog({
      principals: current.principals,
      totalPrincipal: current.totalPrincipal,
      deposit: current.deposit,
      cost: current.cost,
      minBalance: current.minBalance,
      finalBalance: current.finalBalance,
      iterations,
      perf,
    });
    dend();
  }

  return {
    principals: current.principals,
    deposit: current.deposit,
    cost: current.cost,
    minBalance: current.minBalance,
    finalBalance: current.finalBalance,
    balances: current.balances,
    iterations,
    __perf: perf,
  };
}

/* ─────────────────────────────────────────────
   Internal helpers
───────────────────────────────────────────── */

function roundCents(v) {
  return Math.round((Number(v) || 0) * 100) / 100;
}

function isBetter(candidate, incumbent) {
  if (!incumbent) return true;
  if (!candidate) return false;

  if (candidate.cost === Number.POSITIVE_INFINITY) return false;

  const cCost = roundCents(candidate.cost);
  const iCost = roundCents(incumbent.cost);

  if (cCost < iCost - 0.0005) return true;
  if (cCost > iCost + 0.0005) return false;

  const cDep = roundCents(candidate.deposit);
  const iDep = roundCents(incumbent.deposit);
  if (cDep < iDep - 0.0005) return true;
  if (cDep > iDep + 0.0005) return false;

  const cGap = Math.abs(candidate.minBalance - candidate.finalBalance);
  const iGap = Math.abs(incumbent.minBalance - incumbent.finalBalance);
  return cGap < iGap - 0.0005;
}

function approxEqual(a, b, eps = 0.0005) {
  return Math.abs(Number(a || 0) - Number(b || 0)) <= eps;
}
